import { z } from "zod";
import {
  buildPersonName,
  checkIsNotEmpty,
  isNotNullNotUndefined
} from "../helpers/utilities.js";
import { groupIdSchema } from "./group.js";
import {
  playbookIdSchema
} from "./playbook.js";
import { recordingIdSchema } from "./recording.js";
import { gradingIdSchema } from "./teammatePerformanceSummary.js";
import {
  isPredefinedTeammateRoleType,
  predefinedRoleNameByType,
  teammatePermissionSchema,
  TeammateRoleTypes
} from "./teammatePermissions.js";
import {
  savedTeammateVocalIntelligenceSchema
} from "./vocalIntelligence.js";
const TeammateType = {
  REGULAR: "regular",
  AGENT: "agent"
};
const allTeammateTypes = [
  TeammateType.REGULAR,
  TeammateType.AGENT
];
const teammateTypeSchema = z.enum(allTeammateTypes);
const TeammateLicenseType = {
  TEAMMATE: "teammate",
  TEAMMATE_LITE: "teammate_lite",
  MANAGER: "manager"
};
const allTeammateLicenseTypes = [
  TeammateLicenseType.TEAMMATE,
  TeammateLicenseType.TEAMMATE_LITE,
  TeammateLicenseType.MANAGER
];
const teammateLicenseTypeSchema = z.enum(allTeammateLicenseTypes);
const licenseTypeToProductKey = /* @__PURE__ */ new Map(
  [
    [TeammateLicenseType.TEAMMATE, "performance_teammate"],
    [TeammateLicenseType.TEAMMATE_LITE, "performance_teammate_lite"],
    [TeammateLicenseType.MANAGER, "performance_manager"]
  ]
);
const productKeyToLicenseType = new Map(
  [...licenseTypeToProductKey].map(
    ([licenseType, productKey]) => [productKey, licenseType]
  )
);
function getProductKeyForTeammateLicenseType(licenseType) {
  return licenseTypeToProductKey.get(licenseType);
}
function getTeammateLicenseTypeForProductKey(productKey) {
  return productKeyToLicenseType.get(productKey);
}
const teammateIdSchema = z.uuid().brand();
function asTeammateId(id) {
  return teammateIdSchema.parse(id);
}
function getTeammateSsoProvisioningState(settings) {
  const rawState = settings?.sso_provisioning_state;
  if (rawState === null || typeof rawState !== "object" || Array.isArray(rawState)) {
    return { last_provisioned_auth_at: null, pending_manager_email: null };
  }
  const state = rawState;
  const lastProvisionedAuthAt = state.last_provisioned_auth_at;
  const pendingManagerEmail = state.pending_manager_email;
  return {
    last_provisioned_auth_at: typeof lastProvisionedAuthAt === "number" && Number.isSafeInteger(lastProvisionedAuthAt) && lastProvisionedAuthAt >= 0 ? lastProvisionedAuthAt : null,
    pending_manager_email: typeof pendingManagerEmail === "string" && pendingManagerEmail.length > 0 && pendingManagerEmail === pendingManagerEmail.trim().toLowerCase() ? pendingManagerEmail : null
  };
}
function getDefaultTeammateSettings() {
  return {
    chat_id: null,
    teammate_agent_config_id: null,
    vocal_intelligence: [],
    preferences: {
      team_performance_filter_group_id: null,
      team_performance_custom_field_column_ids: null,
      team_performance_section_column_ids: null,
      team_performance_playbook_column_visible: null,
      team_performance_metric_column_ids: null
    }
  };
}
function parseTeammateVocalIntelligence(raw) {
  const candidates = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
  const result = [];
  for (const candidate of candidates) {
    const parsed = savedTeammateVocalIntelligenceSchema.safeParse(candidate);
    if (parsed.success) {
      result.push(parsed.data);
    }
  }
  return result;
}
function mapTeammateDBToTeammate(teammateDB) {
  const mergedSettings = {
    ...getDefaultTeammateSettings(),
    ...typeof teammateDB.settings === "object" ? teammateDB.settings : {}
  };
  const publicSettings = { ...mergedSettings };
  delete publicSettings.sso_provisioning_state;
  const teammate = {
    id: teammateDB.id,
    auth_user_id: teammateDB.auth_user_id,
    email: teammateDB.email,
    company_id: teammateDB.company_id,
    first_name: teammateDB.first_name,
    last_name: teammateDB.last_name,
    profile_picture_url: teammateDB.profile_picture_url,
    created_at: teammateDB.created_at,
    updated_at: teammateDB.updated_at,
    deactivated_at: teammateDB.deactivated_at,
    deleted_at: teammateDB.deleted_at,
    type: teammateDB.type,
    license_type: teammateDB.license_type,
    // Default to editor permissions if no permissions defined for now.
    // TODO: remove the fallback once permissions have been set for all teammates in the db
    permissions: teammateDB.permissions ?? {
      role: {
        type: TeammateRoleTypes.EDITOR
      }
    },
    settings: {
      ...publicSettings,
      vocal_intelligence: parseTeammateVocalIntelligence(
        mergedSettings.vocal_intelligence
      )
    }
  };
  return teammate;
}
function renderTeammateFullName(teammate) {
  return buildPersonName(teammate);
}
function renderTeammateDisplayName(teammate) {
  if (!teammate) {
    return void 0;
  }
  const fullName = renderTeammateFullName(teammate);
  return fullName ?? teammate.email;
}
function buildTeammateDisplayNames(teammate) {
  const name = buildPersonName(teammate);
  const email = teammate.email;
  const displayNames = [name, email].filter(isNotNullNotUndefined).filter(checkIsNotEmpty);
  const primaryDisplayName = displayNames.shift() ?? email;
  const secondaryDisplayName = displayNames.shift();
  return { primaryDisplayName, secondaryDisplayName };
}
function renderTeammateRoleLabel(teammate) {
  const roleType = teammate.permissions.role.type;
  return isPredefinedTeammateRoleType(roleType) ? predefinedRoleNameByType[roleType] : "Custom";
}
function isTeammateAnEditor(teammate) {
  return teammate.permissions.role.type === TeammateRoleTypes.EDITOR;
}
function isTeammateAReviewer(teammate) {
  return teammate.permissions.role.type === TeammateRoleTypes.REVIEWER;
}
function isTeammateATeamMember(teammate) {
  return teammate.permissions.role.type === TeammateRoleTypes.TEAM_MEMBER;
}
const teammatePatchPayloadSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  license_type: teammateLicenseTypeSchema,
  permissions: teammatePermissionSchema,
  preferences: z.object({
    team_performance_filter_group_ids: z.array(groupIdSchema).nullable(),
    /** @deprecated Use `team_performance_filter_group_ids` instead. */
    team_performance_filter_group_id: groupIdSchema.nullable(),
    team_performance_custom_field_column_ids: z.object({
      default: z.array(z.string()).nullable(),
      teammate_agent: z.record(z.string(), z.array(z.string())).nullable()
    }).nullable(),
    team_performance_section_column_ids: z.record(z.uuid(), z.array(z.uuid())).nullable(),
    team_performance_playbook_column_visible: z.record(z.uuid(), z.boolean()).nullable(),
    team_performance_metric_column_ids: z.record(z.uuid(), z.array(z.string())).nullable(),
    team_performance_playbook_id: playbookIdSchema.nullable()
  }).partial(),
  is_active: z.boolean()
}).partial();
const TeammateCopilotTypes = {
  GRADING: "grading",
  LIVE_COACHING: "live_coaching"
};
const allTeammateCopilotTypes = [
  TeammateCopilotTypes.GRADING,
  TeammateCopilotTypes.LIVE_COACHING
];
const teammateCopilotTypeSchema = z.enum(allTeammateCopilotTypes);
const teammateCopilotDetailsSchema = z.object({
  type: z.literal(TeammateCopilotTypes.GRADING),
  grading_id: gradingIdSchema
});
const teammateCopilotMessageSchema = z.object({
  user_chat_message: z.object({
    id: z.uuid(),
    content: z.string()
  }).nullable(),
  chat_details: teammateCopilotDetailsSchema
});
const liveCoachingCopilotDetailsSchema = z.object({
  type: z.literal(TeammateCopilotTypes.LIVE_COACHING),
  grading_id: gradingIdSchema,
  recording_id: recordingIdSchema,
  call_time_millis: z.number().nonnegative()
});
const liveCoachingCopilotMessageSchema = z.object({
  messages: z.array(
    z.object({
      id: z.uuid(),
      role: z.enum(["user", "assistant"]),
      content: z.string()
    })
  ),
  chat_details: liveCoachingCopilotDetailsSchema
});
function toTeammatePublicApi(teammate, managerId, teammateGroups) {
  return {
    id: teammate.id,
    first_name: teammate.first_name,
    last_name: teammate.last_name,
    email: teammate.email,
    manager_id: managerId,
    teammate_groups: [...teammateGroups],
    is_active: !isTeammateDeactivated(teammate)
  };
}
function isTeammateDeactivated(teammate) {
  return isNotNullNotUndefined(teammate.deactivated_at);
}
export {
  TeammateLicenseType,
  TeammateType,
  allTeammateLicenseTypes,
  allTeammateTypes,
  asTeammateId,
  buildTeammateDisplayNames,
  getDefaultTeammateSettings,
  getProductKeyForTeammateLicenseType,
  getTeammateLicenseTypeForProductKey,
  getTeammateSsoProvisioningState,
  isTeammateAReviewer,
  isTeammateATeamMember,
  isTeammateAnEditor,
  isTeammateDeactivated,
  liveCoachingCopilotDetailsSchema,
  liveCoachingCopilotMessageSchema,
  mapTeammateDBToTeammate,
  renderTeammateDisplayName,
  renderTeammateFullName,
  renderTeammateRoleLabel,
  teammateCopilotDetailsSchema,
  teammateCopilotMessageSchema,
  teammateCopilotTypeSchema,
  teammateIdSchema,
  teammateLicenseTypeSchema,
  teammatePatchPayloadSchema,
  teammateTypeSchema,
  toTeammatePublicApi
};
//# sourceMappingURL=teammate.js.map
